import { ApolloClient, ApolloProvider, NormalizedCacheObject } from '@apollo/client'
import cookie from 'js-cookie'
import Head from 'next/head'
import Router, { useRouter } from 'next/router'
import { RefObject, createRef, useEffect, useState } from 'react'
import ReactModal from 'react-modal'
import { Provider } from 'react-redux'
import { Context as ResponsiveContext } from 'react-responsive'
import { v4 as uuidv4 } from 'uuid'
import { setLocale } from 'yup'

import Banner from 'banner/Banner'
import ExperimentProvider from 'experiments/providers/ExperimentProvider'
import FeatureFlagContextProvider from 'featureFlags/providers/FeatureFlagProvider'
import { HyperLinks } from 'head'
import AvantSiteTracking from 'head/components/AvantSiteTracking'
import ImpactTag from 'head/components/ImpactTag'
import OneTrustTag from 'head/components/OneTrustTag'
import SnapchatPixel from 'head/components/SnapchatPixel'
import { LANGUAGE_COOKIE_NAME } from 'localization'
import { getReferrerInfo, getUtmQueryParams } from 'mParticle/mParticleService'
import sendTrackingEvent from 'mParticle/sendTrackingEvent'
import { AppProps } from 'next/app'
import 'normalize.css'
import withApolloClient from 'shared/apollo/withApolloClient'
import ShoppingRegionContextProvider from 'shared/contexts/ShoppingRegionContextProvider'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import withBugsnagClient from 'shared/hocs/withBugsnagClient'
import withLocalization from 'shared/hocs/withLocalization'
import withTracking from 'shared/hocs/withTracking'
import PageLayout from 'shared/layouts/PageLayout'
import 'shared/polyfills.js'
import 'shared/styles/global.css'
import { GetServerPageProps } from 'shared/utils/getServerSideProps'
import { setUser } from 'store/authSlice'
import { setupStore } from 'store/store'

type FCAppProps = {
  appContainerRef: RefObject<HTMLDivElement>
  apolloClient: ApolloClient<NormalizedCacheObject>
  avantSiteID: string
  isDesktop: boolean
}

const store = setupStore({})

const FlightClubApp = ({
  apolloClient,
  avantSiteID,
  Component,
  pageProps,
}: AppProps<GetServerPageProps> & FCAppProps) => {
  const {
    availableCountries,
    availableCurrencies,
    canonicalUrl,
    cmsBanner,
    currentPath,
    featureFlags,
    footerData,
    locale,
    shoppingRegionCountry,
    user,
    width: widthProp,
  } = pageProps
  const appContainerRef = createRef<HTMLDivElement>()
  const [width, setWidth] = useState(widthProp)
  const [clientDedupID, setClientDedupId] = useState(uuidv4())
  const [hasLoggedUtmEvent, setHasLoggedUtmEvent] = useState(false)

  // utm query use effect to replace componentDidUpdate
  useEffect(() => {
    if (!hasLoggedUtmEvent) {
      const utmQueryParams = getUtmQueryParams()
      sendTrackingEvent('CAMPAIGN_SESSION_START', {
        ...getReferrerInfo(),
        ...utmQueryParams,
        client_dedup_id: clientDedupID,
      })
      setHasLoggedUtmEvent(true)
    }
  }, [hasLoggedUtmEvent])

  useEffect(() => {
    if (user && !store.getState()?.auth?.isAuthenticated) {
      store.dispatch(setUser(user))
    }
  }, [user])

  useEffect(() => {
    cookie.set(LANGUAGE_COOKIE_NAME, locale, { path: '/' })
  }, [locale])

  // useEffect to replace componentDidMount
  useEffect(() => {
    setLocale({
      mixed: {
        required: '${path} is a required field.',
      },
    })

    // HACKFINDER: https://github.com/zeit/next.js/issues/3065
    // known issue with back navigation with shallow routing
    Router.beforePopState(({ as }) => {
      window.location.href = as
      window.location.reload()
      return false
    })

    if (appContainerRef.current) {
      ReactModal.setAppElement(appContainerRef.current)
    }

    setWidth(window.innerWidth)
    window.addEventListener('resize', setWidth(window.innerWidth))
    // FIXME: hack to fix the layout of cached html
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
    }, 0)
    return () => {
      // cleanup function
      window.removeEventListener('resize', setWidth)
    }
  }, [])

  const { pathname } = useRouter()
  // FIXME: temp logic for now
  const isCheckout = pathname === '/checkout' || pathname === '/checkoutSuccess'
  const isImpactTrackingEnabled = featureFlags?.[FeatureFlag.IMPACT_TRACKING] ?? false
  const isOneTrustEnabled = featureFlags?.[FeatureFlag.ONE_TRUST] ?? false
  const areHreflangEnabled = featureFlags?.[FeatureFlag.TEMP_WEB_FC_HREFLANG] ?? false
  const baseUrl = 'https://www.flightclub.com'
  const enUSUrl = `${baseUrl}${currentPath}`
  const jaJPUrl = `${baseUrl}/ja-jp${currentPath !== '/' ? currentPath : ''}`
  const enJPUrl = `${baseUrl}/en-jp${currentPath !== '/' ? currentPath : ''}`

  return (
    <div ref={appContainerRef}>
      <Head>
        <title>Sneakers. Here. | Flight Club</title>
        {areHreflangEnabled && (
          <>
            <link rel="alternate" hrefLang="x-default" href={enUSUrl} />
            <link rel="alternate" hrefLang="en-us" href={enUSUrl} />
            <link rel="alternate" hrefLang="ja-jp" href={jaJPUrl} />
            <link rel="alternate" hrefLang="en-jp" href={enJPUrl} />
          </>
        )}
      </Head>
      {avantSiteID && <AvantSiteTracking isOneTrustEnabled={isOneTrustEnabled} />}
      {isOneTrustEnabled && <OneTrustTag />}
      {isImpactTrackingEnabled && <ImpactTag isOneTrustEnabled={isOneTrustEnabled} />}
      <ApolloProvider client={apolloClient}>
        <Provider store={store}>
          <SnapchatPixel isOneTrustEnabled={isOneTrustEnabled} />
          <ResponsiveContext.Provider value={{ width }}>
            <FeatureFlagContextProvider featureFlags={featureFlags}>
              <ShoppingRegionContextProvider
                serverValues={shoppingRegionCountry}
                availableCountries={availableCountries}
                availableCurrencies={availableCurrencies}
                clientSideLocale={locale}
              >
                <ExperimentProvider>
                  <HyperLinks canonicalUrl={canonicalUrl} currentPath={currentPath} />
                  {!isCheckout && <Banner content={cmsBanner} />}
                  <PageLayout footerData={footerData}>
                    <Component {...pageProps} />
                  </PageLayout>
                </ExperimentProvider>
              </ShoppingRegionContextProvider>
            </FeatureFlagContextProvider>
          </ResponsiveContext.Provider>
        </Provider>
      </ApolloProvider>
    </div>
  )
}

// prettier-ignore
export default 
  withApolloClient(
    withTracking(
      withBugsnagClient(
        withLocalization(FlightClubApp)
      )
    )
  )
